// Thin wrapper around y-indexeddb so the rest of the app doesn't need to
// know which local-persistence library we use. The wrapper exposes a single
// promise that resolves once the existing IndexedDB state has been replayed
// into the doc (or immediately if there's no stored state).
//
// One DB per board, namespaced `frame-board:<boardId>`. y-indexeddb dedupes
// updates internally and compacts the on-disk log every 500 entries, so we
// don't need a snapshot scheduler at the v1 scale.

import * as Y from 'yjs';
import { IndexeddbPersistence } from 'y-indexeddb';

const DB_PREFIX = 'frame-board:';

export interface LocalPersistence {
  ready: Promise<void>;
  destroy: () => Promise<void>;
}

export function attachLocalPersistence(boardId: string, doc: Y.Doc): LocalPersistence {
  const dbName = `${DB_PREFIX}${boardId}`;
  const provider = new IndexeddbPersistence(dbName, doc);

  // `whenSynced` resolves after the initial set of stored updates has been
  // applied to `doc`. Callers (BoardPage gate) wait on this before showing
  // the canvas, otherwise shapes pop in after the first render.
  const ready = provider.whenSynced.then(() => undefined);

  return {
    ready,
    destroy: () => provider.destroy().then(() => undefined),
  };
}

// Exposed so the Recent Boards page can offer "clear this board" without
// pulling in y-indexeddb itself.
export async function deleteLocalBoard(boardId: string): Promise<void> {
  if (typeof indexedDB === 'undefined') return;
  const dbName = `${DB_PREFIX}${boardId}`;
  await new Promise<void>((resolve) => {
    const req = indexedDB.deleteDatabase(dbName);
    req.onsuccess = () => resolve();
    req.onerror = () => resolve();
    req.onblocked = () => resolve();
  });
}
